/**
 * Profile picture upload.
 *
 * Two servers are involved, deliberately:
 *
 *   1. The **host app backend** stores the bytes. It already runs S3 for
 *      attachments, and the SDK is bring-your-own-storage by design — it takes
 *      a URL, never a file.
 *   2. **sdk-server** stores the resulting URL on `sdk_contacts`, which is what
 *      presence, chat lists and call screens resolve peer avatars from.
 *
 * Both writes must land or the picture is only half-applied: bytes in the
 * bucket that nobody renders, or a URL pointing at nothing. The upload is the
 * one that can fail expensively (network, image rejected), so it goes first;
 * if the SDK sync then fails we surface it rather than reporting success, since
 * the user would otherwise see their new picture on this screen and their old
 * one everywhere else.
 */

import { config } from '@/config/env'
import { getSdkOrAppToken } from '@/lib/sdk-session-token'

const API = config.apiUrl
const SDK = import.meta.env.VITE_SDK_API_URL || API

function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const session = (JSON.parse(raw) as any)?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

export class AvatarUploadError extends Error {}

/** Pull the server's reason out of the error envelope; fall back to something honest. */
async function reasonFrom(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    return body?.error?.message || fallback
  } catch {
    return fallback
  }
}

/**
 * Mirror the URL onto `sdk_contacts` so presence/chat show the same picture.
 * `null` clears it — which `register()` cannot express, hence the dedicated
 * profile endpoint.
 */
async function syncToSdk(avatarUrl: string | null): Promise<void> {
  const token = await getSdkOrAppToken()
  if (!token) throw new AvatarUploadError('Not signed in')

  const res = await fetch(`${SDK}/api/v1/contacts/self/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ avatarUrl }),
  })
  // 403 means this user has not accepted the chat T&C yet, so they have no
  // sdk_contacts row to write to. That must not fail the whole upload — their
  // inMeet-side picture is set and presence will pick it up at registration.
  if (res.status === 403) return
  if (!res.ok) {
    throw new AvatarUploadError(await reasonFrom(res, 'Picture saved, but chat could not be updated'))
  }
}

/** Upload a new picture. Returns the durable public URL now in force. */
export async function uploadAvatar(file: File): Promise<string> {
  const token = appToken()
  if (!token) throw new AvatarUploadError('Not signed in')

  const form = new FormData()
  form.append('avatar', file)

  const res = await fetch(`${API}/api/v1/users/me/avatar`, {
    method: 'POST',
    credentials: 'include',
    // No Content-Type header: the browser must set it itself so the multipart
    // boundary is included. Setting it by hand makes the body unparseable.
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
  if (!res.ok) {
    throw new AvatarUploadError(await reasonFrom(res, 'Could not upload the picture'))
  }

  const { avatarUrl } = (await res.json()) as { avatarUrl: string }
  await syncToSdk(avatarUrl)
  return avatarUrl
}

/** Remove the picture everywhere; surfaces fall back to initials. */
export async function removeAvatar(): Promise<void> {
  const token = appToken()
  if (!token) throw new AvatarUploadError('Not signed in')

  const res = await fetch(`${API}/api/v1/users/me/avatar`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) {
    throw new AvatarUploadError(await reasonFrom(res, 'Could not remove the picture'))
  }
  await syncToSdk(null)
}

/** Shared client-side guards, so the user hears "too big" before a 5 MB round trip. */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024

export function validateAvatarFile(file: File): string | null {
  if (!file.type.startsWith('image/')) return 'Please choose an image file'
  if (file.size > AVATAR_MAX_BYTES) return 'Image must be under 5 MB'
  return null
}
